/**
 * 睡眠とBody Batteryの複合線グラフ
 *
 * 時系列で睡眠レベルとBody Batteryの変化を同時に表示
 * イベント（睡眠、アクティビティ）による変化も表示
 */

import { format, parseISO } from 'date-fns'
import { Activity, Battery, Moon, TrendingDown, TrendingUp, Zap } from 'lucide-react'
import { Area, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import type { GarminBodyBatteryEvent, GarminBodyBatteryResponse, GarminSleepSummary } from '@/api/types/garmin'
import { cn } from '@/lib/utils'

type Props = {
  sleep: GarminSleepSummary | null
  bodyBattery: GarminBodyBatteryResponse | null
  className?: string
}

// 睡眠レベルの定義
const SLEEP_LEVELS: Record<number, { label: string; color: string }> = {
  0: { label: '深い睡眠', color: 'hsl(250, 80%, 50%)' }, // 紫
  1: { label: '浅い睡眠', color: 'hsl(200, 70%, 50%)' }, // 青
  2: { label: 'REM', color: 'hsl(280, 60%, 60%)' }, // マゼンタ
  3: { label: '覚醒', color: 'hsl(40, 80%, 60%)' }, // オレンジ
}

type ChartDataPoint = {
  time: string
  displayTime: string
  battery: number | null
  sleepLevel: number | null
  sleepLevelLabel: string | null
}

/**
 * 睡眠データとBody Batteryデータを統合したチャートデータを生成
 */
function buildChartData(
  sleep: GarminSleepSummary | null,
  bodyBattery: GarminBodyBatteryResponse | null,
): ChartDataPoint[] {
  const dataMap = new Map<string, ChartDataPoint>()

  // Body Batteryデータを追加
  if (bodyBattery?.timeline) {
    for (const point of bodyBattery.timeline) {
      const time = point.timestamp
      const displayTime = format(parseISO(point.timestamp), 'HH:mm')
      dataMap.set(time, {
        time,
        displayTime,
        battery: point.value,
        sleepLevel: null,
        sleepLevelLabel: null,
      })
    }
  }

  // 睡眠データを追加（各レベルの開始〜終了時間を補間）
  if (sleep?.levels && sleep.levels.length > 0) {
    for (const level of sleep.levels) {
      const startTime = parseISO(level.start_time)
      const endTime = parseISO(level.end_time)

      // 開始時刻にデータポイントを追加
      const startKey = level.start_time
      const existing = dataMap.get(startKey)
      const levelInfo = SLEEP_LEVELS[level.level] || SLEEP_LEVELS[3]

      if (existing) {
        existing.sleepLevel = level.level
        existing.sleepLevelLabel = levelInfo.label
      } else {
        dataMap.set(startKey, {
          time: startKey,
          displayTime: format(startTime, 'HH:mm'),
          battery: null,
          sleepLevel: level.level,
          sleepLevelLabel: levelInfo.label,
        })
      }

      // 終了時刻にもデータポイントを追加（同じレベルで）
      const endKey = level.end_time
      const existingEnd = dataMap.get(endKey)
      if (existingEnd) {
        // 終了時刻では次のレベルが始まるので、現在のレベルは設定しない
        // （次のレベルの開始時刻と同じ場合があるため）
      } else {
        dataMap.set(endKey, {
          time: endKey,
          displayTime: format(endTime, 'HH:mm'),
          battery: null,
          sleepLevel: level.level,
          sleepLevelLabel: levelInfo.label,
        })
      }
    }
  }

  // 時間順にソート
  const sorted = Array.from(dataMap.values()).sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())

  // 睡眠レベルを前の値で補間（分けて寝た日は睡眠の合間を埋めないよう、どれかの区間内の点だけ）
  const segments = (sleep?.levels ?? []).map((l) => ({
    start: parseISO(l.start_time).getTime(),
    end: parseISO(l.end_time).getTime(),
  }))
  const inSleep = (time: string) => {
    const t = parseISO(time).getTime()
    return segments.some((s) => s.start <= t && t <= s.end)
  }
  let lastSleepLevel: number | null = null
  let lastSleepLabel: string | null = null
  for (const point of sorted) {
    if (point.sleepLevel !== null) {
      lastSleepLevel = point.sleepLevel
      lastSleepLabel = point.sleepLevelLabel
    } else if (lastSleepLevel !== null && inSleep(point.time)) {
      point.sleepLevel = lastSleepLevel
      point.sleepLevelLabel = lastSleepLabel
    }
  }

  return sorted
}

// カスタムツールチップ
const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: unknown[]; label?: string }) => {
  if (!active || !payload || payload.length === 0) return null

  const batteryPayload = payload.find((p: unknown) => (p as { dataKey: string }).dataKey === 'battery') as
    | {
        value: number | null
      }
    | undefined
  const sleepPayload = payload.find((p: unknown) => (p as { dataKey: string }).dataKey === 'sleepLevel') as
    | {
        payload: ChartDataPoint
      }
    | undefined

  return (
    <div className="bg-popover border rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium mb-2">{label}</p>
      {batteryPayload?.value !== null && batteryPayload?.value !== undefined && (
        <div className="flex items-center gap-2 text-emerald-600">
          <Battery className="size-4" />
          <span>Body Battery: {batteryPayload.value}</span>
        </div>
      )}
      {sleepPayload?.payload?.sleepLevelLabel && (
        <div className="flex items-center gap-2 text-indigo-600">
          <Moon className="size-4" />
          <span>睡眠: {sleepPayload.payload.sleepLevelLabel}</span>
        </div>
      )}
    </div>
  )
}

export const SleepBatteryChart = ({ sleep, bodyBattery, className }: Props) => {
  const chartData = buildChartData(sleep, bodyBattery)

  // データがない場合
  if (chartData.length === 0) {
    return (
      <div className={cn('rounded-xl border bg-card p-4 shadow-sm', className)}>
        <div className="flex items-center gap-2 mb-3">
          <Moon className="size-5" />
          <Battery className="size-5" />
          <span className="text-sm font-medium">睡眠 & Body Battery</span>
        </div>
        <div className="h-48 flex items-center justify-center text-muted-foreground">データがありません</div>
      </div>
    )
  }

  // Y軸の睡眠レベルをラベルに変換
  const formatSleepLevel = (value: number) => {
    const levelMap = new Map(Object.entries(SLEEP_LEVELS).map(([k, v]) => [Number(k), v]))
    const level = levelMap.get(value)
    return level?.label || ''
  }

  return (
    <div className={cn('rounded-xl border bg-card p-4 shadow-sm', className)}>
      <div className="flex items-center gap-2 mb-3">
        <Moon className="size-5 text-indigo-500" />
        <Battery className="size-5 text-emerald-500" />
        <span className="text-sm font-medium">睡眠 & Body Battery</span>
      </div>

      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <XAxis
              dataKey="displayTime"
              tick={{ fontSize: 10, fill: '#a1a1aa' }}
              stroke="#a1a1aa"
              interval="preserveStartEnd"
              tickCount={6}
            />
            {/* Body Battery Y軸 (左) */}
            <YAxis
              yAxisId="battery"
              orientation="left"
              domain={[0, 100]}
              tick={{ fontSize: 10, fill: '#a1a1aa' }}
              stroke="#a1a1aa"
              tickFormatter={(v) => `${v}`}
            />
            {/* 睡眠レベル Y軸 (右) */}
            <YAxis
              yAxisId="sleep"
              orientation="right"
              domain={[0, 3]}
              tick={{ fontSize: 10, fill: '#a1a1aa' }}
              stroke="#a1a1aa"
              tickFormatter={formatSleepLevel}
              reversed
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
              formatter={(value: string) => (value === 'battery' ? 'Body Battery' : '睡眠レベル')}
            />
            {/* 睡眠レベル (エリア) */}
            <Area
              yAxisId="sleep"
              type="stepAfter"
              dataKey="sleepLevel"
              stroke="hsl(250, 60%, 60%)"
              fill="hsl(250, 60%, 60%)"
              fillOpacity={0.2}
              strokeWidth={0}
              connectNulls={false}
              name="睡眠レベル"
            />
            {/* Body Battery (ライン) */}
            <Line
              yAxisId="battery"
              type="monotone"
              dataKey="battery"
              stroke="hsl(160, 60%, 45%)"
              strokeWidth={2}
              dot={false}
              connectNulls
              name="battery"
            />
            {/* イベントの参照線 */}
            {bodyBattery?.events?.map((event, idx) => {
              if (!event.start_time) return null
              try {
                const eventTime = format(parseISO(event.start_time), 'HH:mm')
                const isCharge = event.impact > 0
                return (
                  <ReferenceLine
                    key={`event-${idx}`}
                    x={eventTime}
                    yAxisId="battery"
                    stroke={isCharge ? 'hsl(160, 70%, 50%)' : 'hsl(0, 70%, 60%)'}
                    strokeDasharray="3 3"
                    strokeWidth={1.5}
                  />
                )
              } catch {
                return null
              }
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* イベント一覧 */}
      {bodyBattery?.events && bodyBattery.events.length > 0 && (
        <div className="mt-4 space-y-2">
          <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <Zap className="size-3" />
            Body Battery 変化イベント
          </h4>
          <div className="grid gap-2">
            {bodyBattery.events.map((event, idx) => (
              <EventCard key={idx} event={event} />
            ))}
          </div>
        </div>
      )}

      {/* 睡眠レベルの凡例 */}
      <div className="flex flex-wrap gap-2 mt-3 text-xs">
        {Object.entries(SLEEP_LEVELS).map(([level, { label, color }]) => (
          <div key={level} className="flex items-center gap-1">
            <div className="size-3 rounded" style={{ backgroundColor: color, opacity: 0.6 }} />
            <span className="text-muted-foreground">{label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// イベントカードコンポーネント
const EventCard = ({ event }: { event: GarminBodyBatteryEvent }) => {
  const isCharge = event.impact > 0
  const eventTypeLabel = getEventTypeLabel(event.event_type)

  const startTime = event.start_time ? format(parseISO(event.start_time), 'HH:mm') : '--:--'
  const duration =
    event.duration_minutes >= 60
      ? `${Math.floor(event.duration_minutes / 60)}時間${event.duration_minutes % 60}分`
      : `${event.duration_minutes}分`

  return (
    <div
      className={cn('flex items-center gap-3 p-2 rounded-lg text-xs', isCharge ? 'bg-emerald-500/10' : 'bg-red-500/10')}
    >
      <div className={cn('p-1.5 rounded-full', isCharge ? 'bg-emerald-500/20' : 'bg-red-500/20')}>
        <EventTypeIcon
          eventType={event.event_type}
          className={cn('size-4', isCharge ? 'text-emerald-600' : 'text-red-600')}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium">{eventTypeLabel}</div>
        <div className="text-muted-foreground">
          {startTime} · {duration}
        </div>
      </div>
      <div className={cn('flex items-center gap-1 font-bold', isCharge ? 'text-emerald-600' : 'text-red-600')}>
        {isCharge ? <TrendingUp className="size-4" /> : <TrendingDown className="size-4" />}
        {isCharge ? '+' : ''}
        {event.impact}
      </div>
    </div>
  )
}

// イベントタイプのラベルを取得
function getEventTypeLabel(eventType: string): string {
  const labelsMap = new Map([
    ['SLEEP', '睡眠'],
    ['ACTIVITY', 'アクティビティ'],
    ['STRESS', 'ストレス'],
    ['REST', '休息'],
    ['UNKNOWN', '不明'],
  ])
  return labelsMap.get(eventType) || eventType
}

// イベントタイプのアイコンを描画するコンポーネント
function EventTypeIcon({ eventType, className }: { eventType: string; className?: string }) {
  switch (eventType) {
    case 'SLEEP':
      return <Moon className={className} />
    case 'ACTIVITY':
      return <Activity className={className} />
    case 'STRESS':
      return <Zap className={className} />
    case 'REST':
      return <Battery className={className} />
    default:
      return <Zap className={className} />
  }
}
